/**
 * @typedef {import('../CondicoesEspeciais/ModalCondicoesEspeciais').CondicoesEspeciais} CondicoesEspeciais
 */
import React, { Fragment, useEffect, useState } from 'react';
import { Alert, Col, Row } from 'react-bootstrap';
import { MdKeyboardArrowRight, MdWarning } from 'react-icons/md';
import { FaStar } from 'react-icons/fa';
import defaultLogo from '~/Images/logo.svg';
import { formatarMonetario } from '~/Utils/Helpers.js';
import { descreverCampoAtivo } from '~/Pages/Vendas/helpers';
import { Container } from './styles';
import _ from 'lodash';
import moment from 'moment';
import {
  CondicoesEspeciaisStatus,
  PeriodicidadeBalao,
  TiposParcelasFinanciamento,
} from '~/constant';
import { TipoComissao } from '~/constant';

/**
 * W-0981 SetClub: antes o rótulo era `=== 1 ? 'Construção' : 'Adimplência'`,
 * então a condicionante SetClube (balão 3) aparecia como "Adimplência".
 */
const descreverTipoBalao = periodicidadeBalao => {
  const tipo = Number(periodicidadeBalao);
  if (tipo === PeriodicidadeBalao.Construcao) return 'Construção';
  if (tipo === PeriodicidadeBalao.Adimplencia) return 'Adimplência';
  if (tipo === PeriodicidadeBalao.SetClube) return 'SetClube';
  return '';
};

/**
 * @param {{condicoesEspeciais: CondicoesEspeciais}}
 */
export default function ResumoVenda({
  empreendimento,
  tabelaSelecionada,
  totalVenda,
  totalTitulosSinal,
  totalTituloSinalIncorp,
  ativosSelecionados,
  ativosPorCodigo,
  ativosDaTabelaSelecionadaPorCodigo,
  permissaoAvancar,
  quandoAvancar,
  prospecto,
  etapaAtual,
  etapas,
  listaMotivosAutorizacao,
  condicoesEspeciais,
  financiamentoAnterior,
  financiamentoAtual,
  readonly,
  travarVenda,
  carregandoPropostaInicial
}) {
  const etapaFinal = etapas[etapas.length - 1];
  const {
    descricao,
    financiamento,
    isIsencao,
    saldo_correcao,
    temCondicao,
    valor_lote,
  } = condicoesEspeciais;

  const mostrarResumoCondicaoEspecial = () => {
    if (Array.isArray(condicoesEspeciais)) {
      return condicoesEspeciais.length > 0;
    } else if (typeof condicoesEspeciais === 'object') {
      if (condicoesEspeciais.descricao) {
        return true;
      }
    }
    return false;
  };
  const [mudouValorFinanciamento, setMudouValorFinanciamento] = useState(false);

  const obterDescontoSetclub = codigoAtivo =>
    financiamentoAtual &&
    financiamentoAtual[codigoAtivo] &&
    financiamentoAtual[codigoAtivo].descontoSetclub;

  useEffect(() => {
    const codigo_ativo = Object.keys(ativosDaTabelaSelecionadaPorCodigo)[0];

    if (codigo_ativo && financiamentoAnterior && financiamentoAtual) {
      const parcelaAnterior =
        (financiamentoAnterior &&
          financiamentoAnterior[codigo_ativo] &&
          financiamentoAnterior[codigo_ativo].parcelasFinanciamento) ||
        false;

      const parcelaAtual =
        (financiamentoAtual &&
          financiamentoAtual[codigo_ativo] &&
          financiamentoAtual[codigo_ativo].parcelasFinanciamento) ||
        false;

      const valorSomaParcelasFinanciamentoAnterior = () => {
        if (!parcelaAnterior) return 0;

        return parcelaAnterior
          .filter(
            element =>
              element.tipo_parcela_financiamento ===
              TiposParcelasFinanciamento.Financiamento
          )
          .reduce((acc, element) => acc + (element.valor || 0), 0);
      };

      const valorSomaParcelasFinanciamentoAtual = () => {
        if (!parcelaAtual) return 0;

        return parcelaAtual
          .filter(
            element =>
              element.tipo_parcela_financiamento ===
              TiposParcelasFinanciamento.Financiamento
          )
          .reduce((acc, element) => acc + (element.valor || 0), 0);
      };

      setMudouValorFinanciamento(
        valorSomaParcelasFinanciamentoAnterior() !=
          valorSomaParcelasFinanciamentoAtual()
      );
      return;
    }
  }, [financiamentoAnterior, financiamentoAtual, quandoAvancar]);
  const formatarData = data => {
    return new Date(data).toLocaleDateString('pt-BR');
  };

  const agruparParcelasPorValorEDatas = parcelas => {
    if (parcelas.length === 0) return [];

    parcelas.sort((a, b) => {
      if (a.valor !== b.valor) return a.valor - b.valor;
      return new Date(a.vencimento) - new Date(b.vencimento);
    });

    const agrupadas = [];
    let grupoAtual = {
      quantidade: 1,
      valor: parcelas[0].valor,
      dataInicial: parcelas[0].vencimento,
      vencimento: parcelas[0].vencimento,
    };

    for (let i = 1; i < parcelas.length; i++) {
      const parcelaAtual = parcelas[i];

      if (parcelaAtual.valor === grupoAtual.valor) {
        grupoAtual.quantidade += 1;
        grupoAtual.vencimento = parcelaAtual.vencimento;
      } else {
        agrupadas.push(grupoAtual);
        grupoAtual = {
          quantidade: 1,
          valor: parcelaAtual.valor,
          dataInicial: parcelaAtual.vencimento,
          vencimento: parcelaAtual.vencimento,
        };
      }
    }

    agrupadas.push(grupoAtual);
    return agrupadas;
  };

  const agruparParcelasFinanciamento = parcelas => {
    const agrupadas = {};
    parcelas.forEach(parcela => {
      const valorKey = parcela.valor;
      if (!agrupadas[valorKey]) {
        agrupadas[valorKey] = 1;
      } else {
        agrupadas[valorKey] += 1;
      }
    });
    return agrupadas;
  };

  const gerarFinanciamentoAnterior = financiamentoAnterior => {
    const financiamento =
      financiamentoAnterior[Object.keys(financiamentoAnterior)[0]];
    const parcelas = financiamento.parcelasFinanciamento || [];

    const parcelasCondicionantes = parcelas.filter(
      f =>
        f.tipo_parcela_financiamento ===
        TiposParcelasFinanciamento.Condicionante
    );
    const parcelasIntermediarias = parcelas.filter(
      f =>
        f.tipo_parcela_financiamento ===
        TiposParcelasFinanciamento.Intermediaria
    );
    const parcelasFinanciamento = parcelas.filter(
      f =>
        f.tipo_parcela_financiamento ===
        TiposParcelasFinanciamento.Financiamento
    );
    const parcelasProgramadas = parcelas.filter(
      f =>
        f.tipo_parcela_financiamento === TiposParcelasFinanciamento.Programadas
    );

    const parcelasProgramadasAgrupadas = agruparParcelasPorValorEDatas(
      parcelasProgramadas
    );
    const parcelasIntermediariasAgrupadas = agruparParcelasPorValorEDatas(
      parcelasIntermediarias
    );
    const parcelasFinanciamentoAgrupadas = agruparParcelasFinanciamento(
      parcelasFinanciamento
    );

    const dataInicialFinanciamento =
      parcelasFinanciamento.length > 0
        ? parcelasFinanciamento[0].vencimento
        : null;
    const dataFinalFinanciamento =
      parcelasFinanciamento.length > 0
        ? parcelasFinanciamento[parcelasFinanciamento.length - 1].vencimento
        : null;

    if (
      parcelasCondicionantes.length === 0 &&
      parcelasIntermediarias.length === 0 &&
      parcelasFinanciamento.length === 0 &&
      parcelasProgramadas.length === 0
    ) {
      return null;
    }

    return (
      <Alert variant="success" className="mt-2">
        <h5>Financiamento Anterior</h5>

        {parcelasCondicionantes.length > 0 && (
          <>
            <small>
              <strong>Parcelas Condicionantes</strong>
            </small>
            {parcelasCondicionantes.map((parcela, index) => (
              <div key={index}>
                <small>
                  Tipo balão:{' '}
                  <strong>
                    {descreverTipoBalao(parcela.periodicidade_balao)}
                  </strong>
                </small>
                <br />
                <small className="mt-1">
                  Valor projetado:{' '}
                  <strong>{formatarMonetario(parcela.valor)}</strong>
                </small>
                <br />
                <small className="mt-1">
                  Valor descontado:{' '}
                  <strong>{formatarMonetario(parcela.valor_presente)}</strong>
                </small>
              </div>
            ))}
          </>
        )}
        {parcelasProgramadasAgrupadas.length > 0 && (
          <>
            <hr />
            <small>
              <strong>Parcelas Programadas</strong>
            </small>
            {parcelasProgramadasAgrupadas.map((grupo, index) => (
              <div key={index}>
                <small>
                  {grupo.quantidade}X de{' '}
                  <strong>{formatarMonetario(grupo.valor)}</strong>
                </small>
                <br />
                <small>
                  Inicial: <strong>{formatarData(grupo.dataInicial)}</strong>
                </small>{' '}
                <small>
                  Final: <strong>{formatarData(grupo.vencimento)}</strong>
                </small>
              </div>
            ))}
          </>
        )}
        {parcelasIntermediariasAgrupadas.length > 0 && (
          <>
            <hr />
            <small>
              <strong>Parcelas Intermediárias</strong>
            </small>
            {parcelasIntermediariasAgrupadas.map((grupo, index) => (
              <div key={index}>
                <small>
                  {grupo.quantidade}X de{' '}
                  <strong>{formatarMonetario(grupo.valor)}</strong>
                </small>
                <br />
                <small>
                  Inicial: <strong>{formatarData(grupo.dataInicial)}</strong>
                </small>
                <br />
                <small>
                  Final: <strong>{formatarData(grupo.vencimento)}</strong>
                </small>
              </div>
            ))}
          </>
        )}

        {Object.keys(parcelasFinanciamentoAgrupadas).length > 0 && (
          <>
            <hr></hr>
            <small>
              <strong>Parcelas do Financiamento</strong>
            </small>
            {Object.entries(parcelasFinanciamentoAgrupadas).map(
              ([valor, quantidade], index) => (
                <div key={index}>
                  <small>
                    {quantidade}X de{' '}
                    <strong>{formatarMonetario(Number(valor))}</strong>
                  </small>
                  <br />
                  <small>
                    Inicial:{' '}
                    <strong>{formatarData(dataInicialFinanciamento)}</strong>
                  </small>
                  <br />
                  <small>
                    Final:{' '}
                    <strong>{formatarData(dataFinalFinanciamento)}</strong>
                  </small>
                </div>
              )
            )}
          </>
        )}
      </Alert>
    );
  };

  /**
   * Função que exibe um card que mostra para quem a notificação irá sobre a venda
   */
  const gerarAvisoDeGravarVenda = () => {
    if (
      !Object.keys(condicoesEspeciais).length > 0 ||
      condicoesEspeciais.status != 0
    )
      return;

    const notificarDiretoria = !!(
      travarVenda.travarGerente ||
      isIsencao ||
      saldo_correcao.valor_saldo_correcao > 0
    );
    const notificarGerente = travarVenda.travarCorretor && !notificarDiretoria;

    if (!notificarDiretoria && !notificarGerente) return;
    return (
      <>
        <Alert variant="success" className="mt-2">
          <h5>Notificação da venda</h5>
          <div>
            <small>
              <strong>
                A venda será gravada e notificará{' '}
                {notificarDiretoria ? 'a Diretoria' : 'o seu gerente'}
              </strong>
            </small>
          </div>
        </Alert>
      </>
    );
  };

  return (
    <Container>
      <ul className="list-group mb-3">
        <li className="list-group-item d-flex justify-content-between">
          <div>
            <small>Empreendimento</small>
            <strong className="text-dark d-block">
              {empreendimento.descricao}
            </strong>
          </div>
          <div className="img-resumo-imobiliaria">
            <img
              src={empreendimento.logo ? empreendimento.logo : defaultLogo}
              title={empreendimento.descricao}
              alt={empreendimento.descricao}
            />
          </div>
        </li>

        {prospecto && (
          <li className="list-group-item d-flex justify-content-between">
            <div>
              <small>Cliente</small>
              <strong className="text-dark d-block">{prospecto.nome}</strong>
              {empreendimento && empreendimento.is_setclub && (
                <span
                  className="d-inline-flex align-items-center mt-1"
                  style={{
                    backgroundColor: '#fbf8ccff',
                    border: '1px solid #d4af37',
                    borderRadius: 4,
                    color: '#a88c2fff',
                    fontSize: 12,
                    fontWeight: 700,
                    padding: '2px 8px',
                  }}
                >
                  <FaStar size={12} className="mr-1" />
                  Cliente SetClube
                </span>
              )}
            </div>
          </li>
        )}

        {tabelaSelecionada && ativosSelecionados.length > 0 && (
          <>
            <li className="list-group-item d-flex justify-content-between">
              <div>
                <small>Tabela</small>
                <strong className="text-dark d-block">
                  {tabelaSelecionada.descricao}
                </strong>
              </div>
            </li>
            <li className="list-group-item d-flex justify-content-between bg-light py-1">
              <span>Lotes</span>
              <span className="badge badge-dark badge-pill">
                {ativosSelecionados ? ativosSelecionados.length : 0}
              </span>
            </li>
          </>
        )}

        {ativosSelecionados.map((item, index) => (
          <Fragment key={index}>
            <li className="list-group-item">
              <span className="d-block text-very-small">#{item}</span>
              <div className="d-flex justify-content-between">
                <div>
                  <span>
                    Quadra{' '}
                    <strong className="text-dark">
                      {descreverCampoAtivo(ativosPorCodigo[item], 'quadra')}
                    </strong>{' '}
                    / Lote{' '}
                    <strong className="text-dark">
                      {descreverCampoAtivo(ativosPorCodigo[item], 'lote')}
                    </strong>
                  </span>
                  {!ativosPorCodigo[item] && (
                    <small className="d-block text-warning">
                      <MdWarning className="mr-1" />
                      Dados do lote não disponíveis nesta proposta.
                    </small>
                  )}
                  {tabelaSelecionada && (
                    <small className="d-block">
                      Entrada{' '}
                      <strong className="text-dark">
                        {formatarMonetario(
                          ativosDaTabelaSelecionadaPorCodigo[item].valor_sinal
                        )}
                      </strong>
                    </small>
                  )}
                </div>
                {tabelaSelecionada && (
                  <div>
                    <span className="my-auto">
                      <strong className="text-dark">
                        {formatarMonetario(
                          ativosDaTabelaSelecionadaPorCodigo[item].valor_venda
                        )}
                      </strong>
                    </span>
                    {empreendimento.plano_selecionado && (
                      <small className="d-block">
                        {`${empreendimento.plano_selecionado}x R$ `}
                        <strong className="text-dark">
                          {formatarMonetario(
                            ativosDaTabelaSelecionadaPorCodigo[item].valor_sinal
                          )}
                        </strong>
                      </small>
                    )}
                  </div>
                )}
              </div>
            </li>
            {tabelaSelecionada &&
              ativosDaTabelaSelecionadaPorCodigo[item].desconto && (
                <>
                  {!!ativosDaTabelaSelecionadaPorCodigo[item].desconto
                    .valor_venda && (
                    <>
                      {ativosDaTabelaSelecionadaPorCodigo[item].desconto
                        .valor_venda > 0 ? (
                        <li className="list-group-item d-flex justify-content-between bg-light">
                          <div className="text-success">
                            <h6 className="my-0">Desconto</h6>
                            <small>VALOR DA VENDA</small>
                          </div>
                          <span className="my-auto text-success">
                            -
                            <strong>
                              {formatarMonetario(
                                ativosDaTabelaSelecionadaPorCodigo[item]
                                  .desconto.valor_venda
                              )}
                            </strong>
                          </span>
                        </li>
                      ) : (
                        <li className="list-group-item d-flex justify-content-between bg-light">
                          <div className="text-warning">
                            <h6 className="my-0">Acréscimo</h6>
                            <small>VALOR DA VENDA</small>
                          </div>
                          <span className="my-auto text-warning">
                            <strong>
                              {formatarMonetario(
                                Math.abs(
                                  ativosDaTabelaSelecionadaPorCodigo[item]
                                    .desconto.valor_venda
                                )
                              )}
                            </strong>
                          </span>
                        </li>
                      )}
                    </>
                  )}
                  {!!ativosDaTabelaSelecionadaPorCodigo[item].desconto
                    .valor_sinal && (
                    <li className="list-group-item d-flex justify-content-between bg-light">
                      <div className="text-success">
                        <h6 className="my-0">Desconto</h6>
                        <small>SINAL/ENTRADA</small>
                      </div>
                      <span className="my-auto text-success">
                        -
                        <strong>
                          {formatarMonetario(
                            ativosDaTabelaSelecionadaPorCodigo[item].desconto
                              .valor_sinal
                          )}
                        </strong>
                      </span>
                    </li>
                  )}
                </>
              )}
            {tabelaSelecionada && obterDescontoSetclub(item) && (
              <li className="list-group-item d-flex justify-content-between bg-light">
                <div className="text-success">
                  <h6 className="my-0">SetClube 5%</h6>
                  <small>DESCONTO NO VV</small>
                </div>
                <span className="my-auto text-success">
                  -
                  <strong>
                    {formatarMonetario(obterDescontoSetclub(item).valor)}
                  </strong>
                </span>
              </li>
            )}
          </Fragment>
        ))}
        {tabelaSelecionada && (
          <>
            {totalVenda > 0 && (
              <li className="list-group-item d-flex justify-content-between">
                <span>Valor da venda</span>
                <span className="my-auto">
                  <strong className="text-dark">
                    {formatarMonetario(totalVenda)}
                  </strong>
                </span>
              </li>
            )}
            {empreendimento.tipo_recebimento_comissao ===
              TipoComissao.ComissaoContrato2 &&
              totalTitulosSinal > 0 && (
                <li className="list-group-item d-flex justify-content-between">
                  <span>Entrada</span>
                  <span className="my-auto">
                    <strong className="text-dark">
                      {formatarMonetario(totalTitulosSinal)}
                    </strong>
                  </span>
                </li>
              )}
            {empreendimento.tipo_recebimento_comissao ===
              TipoComissao.RetidaCorretor && (
              <div>
                {totalTitulosSinal > 0 && (
                  <li className="list-group-item d-flex justify-content-between">
                    <span>Entrada Imobiliária</span>
                    <span className="my-auto">
                      <strong className="text-dark">
                        {formatarMonetario(totalTitulosSinal)}
                      </strong>
                    </span>
                  </li>
                )}
                {totalTituloSinalIncorp > 0 && (
                  <li className="list-group-item d-flex justify-content-between">
                    <span>Entrada Incorporadora</span>
                    <span className="my-auto">
                      <strong className="text-dark">
                        {formatarMonetario(totalTituloSinalIncorp)}
                      </strong>
                    </span>
                  </li>
                )}
                {totalTitulosSinal > 0 && totalTituloSinalIncorp > 0 && (
                  <li className="list-group-item d-flex justify-content-between">
                    <span>Total Entrada</span>
                    <span className="my-auto">
                      <strong className="text-dark">
                        {formatarMonetario(
                          totalTitulosSinal + totalTituloSinalIncorp
                        )}
                      </strong>
                    </span>
                  </li>
                )}
              </div>
            )}
          </>
        )}
      </ul>

      {etapaAtual !== etapaFinal && (
        <>
          {!permissaoAvancar.permitir && (
            <small className="text-muted">{permissaoAvancar.mensagem}</small>
          )}
          <button
            className={`btn btn-${
              permissaoAvancar.permitir ? 'primary' : 'secondary'
            } btn-block text-uppercase justify-content-between d-flex align-items-center py-3 px-4 shadow-sm`}
            type="button"
            onClick={quandoAvancar}
            disabled={!permissaoAvancar.permitir || carregandoPropostaInicial}
          >
            Avançar <MdKeyboardArrowRight />
          </button>
        </>
      )}
      {etapaAtual !== etapas[0] && gerarAvisoDeGravarVenda()}
      {etapaAtual !== etapas[0] &&
        listaMotivosAutorizacao.length > 0 &&
        ativosSelecionados.length > 0 && (
          <Alert variant="warning" className="mt-3">
            <p className="text">
              <MdWarning className="mr-1" /> Esta venda requer autorização pelos
              seguintes motivos:
            </p>
            <ol className="pl-3">
              {listaMotivosAutorizacao.map((motivo, index) => (
                <li key={index}>{motivo.descricao}</li>
              ))}
            </ol>
          </Alert>
        )}

      {mostrarResumoCondicaoEspecial() && etapaAtual !== etapas[0] && (
        <>
          <Alert variant="primary" className="mt-2">
            <h5>Condições especiais</h5>
            <div>
              <small>
                Isenção da taxa de cessão:{' '}
                <strong>
                  {/* W-0981: forma canônica única, garantida por
                  `normalizarCondicoesEspeciais` (Store/Vendas/actions.js). */}
                  {isIsencao ? 'Sim' : 'Não'}
                </strong>
              </small>
            </div>
            <div>
              <small>
                Descrição: <strong>{descricao}</strong>
              </small>
            </div>
            {financiamento && financiamento.prazo_financiamento_meses > 0 && (
              <>
                <hr />
                <div>
                  <small>
                    Carência entre Sinal e Fin. (meses):{' '}
                    <strong>
                      {(financiamento &&
                        financiamento.prazo_financiamento_meses) ||
                        '0'}
                    </strong>
                  </small>
                </div>
              </>
            )}
            {saldo_correcao && saldo_correcao.valor_saldo_correcao > 0 && (
              <>
                <hr />
                <div>
                  <small>
                    Saldo correção:{' '}
                    <strong>
                      {saldo_correcao && saldo_correcao.valor_saldo_correcao
                        ? formatarMonetario(saldo_correcao.valor_saldo_correcao)
                        : 'R$ 0,00'}
                    </strong>
                  </small>
                </div>
                <div>
                  <small>
                    Data saldo:{' '}
                    <strong>
                      {(saldo_correcao &&
                        (moment(
                          saldo_correcao.data_saldo,
                          saldo_correcao.data_saldo.includes('/')
                            ? 'MM/YYYY'
                            : undefined
                        ).isValid()
                          ? moment(
                              saldo_correcao.data_saldo,
                              saldo_correcao.data_saldo.includes('/')
                                ? 'MM/YYYY'
                                : undefined
                            ).format('MM/YYYY')
                          : 'Data inválida')) ||
                        '0'}
                    </strong>
                  </small>
                </div>
              </>
            )}
            {valor_lote && valor_lote.valor_sugerido > 0 && (
              <>
                <hr />
                <div>
                  <small>
                    Valor sugerido:{' '}
                    <strong>
                      {valor_lote && valor_lote.valor_sugerido
                        ? formatarMonetario(valor_lote.valor_sugerido)
                        : 'R$ 0,00'}
                    </strong>
                  </small>
                </div>
                <div>
                  <small>
                    Porcentagem de desconto:{' '}
                    <strong>
                      {(valor_lote && valor_lote.porcentagem_desconto) || '0'}%
                    </strong>
                  </small>
                </div>
              </>
            )}
          </Alert>
        </>
      )}

      {!!(
        readonly &&
        mudouValorFinanciamento &&
        condicoesEspeciais &&
        condicoesEspeciais.status ==
          CondicoesEspeciaisStatus.PendenteDiretoria &&
        (etapaAtual == etapas[2] || etapaAtual == etapas[3])
      ) && gerarFinanciamentoAnterior(financiamentoAnterior)}
    </Container>
  );
}
